import React from 'react';
import { createRoot } from 'react-dom/client';

import * as Sentry from '@sentry/react';
import TagManager from 'react-gtm-module';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { history } from './utils/history';
import { Auth0Adapter, TokenAdapter } from 'auth';
import { LocationProvider, browserLocation } from 'services/location';
import { StorageProvider } from 'services/storage';
import authConfig from './auth-config.json';
import { AuthorizedApolloProvider } from './authorized-apollo-client';
import './i18n';
import { calculateOptimalStakeholderPosition } from 'utils/stakeholderPlacementOptimizer';

// Disable console.log in production
if (import.meta.env.VITE_APP_ENV === 'production') {
	console.log = () => { };
}

// @ts-ignore
let subdomain: string = document.location.hostname.replace(/\.gerositus.*/, '');
const config =
	import.meta.env.VITE_APP_MODE === 'corporate'
		? authConfig[import.meta.env.VITE_APP_MODE][
		import.meta.env.VITE_APP_CLIENT || subdomain
		][import.meta.env.VITE_APP_ENV]
		: authConfig[import.meta.env.VITE_APP_MODE][import.meta.env.VITE_APP_ENV];

const onRedirectCallback = (appState?: { targetUrl?: string }) => {
	history.push(appState?.targetUrl ?? browserLocation.getPathname());
};

if (document.location.host != 'localhost:3000') {
	Sentry.init({
		dsn: 'https://33d74f02ec6b4a5dbe4c7317eaede7dc@o23207.ingest.sentry.io/6492582',
		integrations: [
			Sentry.replayIntegration({
				// Privacy: mask all text + inputs before sending the DOM replay
				// to Sentry. Project content (stakeholders, risks, AI responses)
				// must never leak to a third-party.
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true
			})
		],

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for performance monitoring.
		// We recommend adjusting this value in production
		tracesSampleRate: 0.1,
		environment: import.meta.env.VITE_SENTRY_ENV || 'dev',
		ignoreErrors: ['Response not successful: Received status code 401'],
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 1.0
	});
}

if (import.meta.env.VITE_APP_GTM) {
	const tagManagerArgs = {
		gtmId: 'GTM-' + import.meta.env.VITE_APP_GTM
	};

	TagManager.initialize(tagManagerArgs);
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const root = createRoot(rootElement);

// Expose utilities on window for debugging/usage in browser console
// Avoid overriding if already set (e.g., by tests/hot-reload)
if (typeof window !== 'undefined' && !window.calculateOptimalStakeholderPosition) {
	window.calculateOptimalStakeholderPosition = calculateOptimalStakeholderPosition;
}

// Choix du provider d'authentification basé sur la configuration
const isTokenMode = import.meta.env.VITE_APP_FEATURE_TOKEN_LOGIN === 'enabled';

const AuthProviderContent = () => {
	if (isTokenMode) {
		return (
			<TokenAdapter>
				<AuthorizedApolloProvider>
					<App
						returnTo={config.returnTo}
						paymentPublicKey={config.paymentPublicKey}
					/>
				</AuthorizedApolloProvider>
			</TokenAdapter>
		);
	}

	return (
		<Auth0Adapter
			domain={config.domain}
			clientId={config.clientID}
			redirectUri={config.redirectUri}
			audience={config.audience}
			onRedirectCallback={onRedirectCallback}
		>
			<AuthorizedApolloProvider>
				<App
					returnTo={config.returnTo}
					auth0Domain={config.domain}
					paymentPublicKey={config.paymentPublicKey}
				/>
			</AuthorizedApolloProvider>
		</Auth0Adapter>
	);
};

root.render(
	<React.StrictMode>
		<StorageProvider>
			<LocationProvider>
				<AuthProviderContent />
			</LocationProvider>
		</StorageProvider>
	</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
